<template>
  <ViewerLayout>
    <div class="viewer-shell">
      <section class="app-content">
        <article class="viewer-panel">
          <div ref="viewerContainer" class="viewer-canvas"></div>
          <div class="viewer-status" v-if="state.loadingViewer">
            <span>Chargement du modèle IFC...</span>
          </div>
          <p class="empty" v-if="!state.modeles.length && !state.loadingModeles">
            Aucun modèle disponible pour ce site.
          </p>
        </article>
      </section>

      <section class="bottom-panel">
        <div class="status-section">
          <h3>Statuts : </h3>
          <ul class="legend">
            <li v-for="status in statusPalette" :key="status.code">
              <span class="color" :style="{ backgroundColor: status.color }" />
              <strong>{{ status.label }}</strong>
              <small v-if="statusStats.counts[status.code]">
                {{ statusStats.counts[status.code] }}
              </small>
            </li>
          </ul>
          <div class="actions">
            <BaseButton @click="goToProjects" variant="secondary">
              Retour aux projets
            </BaseButton>
          </div>
        </div>
      </section>

      <p v-if="state.error" class="error-banner">
        {{ state.error }}
      </p>
    </div>
  </ViewerLayout>
</template>

<script setup lang="ts">
import { IfcViewerAPI } from 'web-ifc-viewer';
import { Color } from 'three';
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ViewerLayout from '../components/ViewerLayout.vue';
import BaseButton from '../components/BaseButton.vue';
import { useAuth } from '../composables/useAuth';
import { apiFetch } from '../utils/api';

const { getAuthHeaders } = useAuth();
const route = useRoute();
const router = useRouter();

type ModeleSite = {
  id: number;
  siteId: number;
  cheminIfc: string;
  nomFichierOriginal: string;
  estActuel: boolean;
  creeLe: string;
};

type ElementInfo = {
  id: number;
  idGlobalIfc: string;
  nom: string | null;
  typeIfc: string | null;
  niveau: string | null;
  statut: string | null;
};

const statusPalette = [
  { code: 'PLANIFIE', label: 'Planifié', color: '#9aa0a6' },
  { code: 'EN_PRODUCTION', label: 'En production', color: '#fbbc04' },
  { code: 'PRODUIT', label: 'Produit', color: '#1a73e8' },
  { code: 'EN_TRANSIT', label: 'En transit', color: '#4285f4' },
  { code: 'LIVRE', label: 'Livré', color: '#34a853' },
  { code: 'INSTALLE', label: 'Installé', color: '#0f9d58' },
  { code: 'PROBLEME', label: 'Problème', color: '#ea4335' }
] as const;

const viewerContainer = ref<HTMLDivElement | null>(null);
let viewer: IfcViewerAPI | null = null;
let currentObjectUrl: string | null = null;

const state = reactive({
  siteId: '',
  modeles: [] as ModeleSite[],
  selectedModeleId: null as number | null,
  elements: [] as ElementInfo[],
  loadingModeles: false,
  loadingViewer: false,
  error: ''
});

const statusStats = computed(() => {
  const counts: Record<string, number> = {};
  state.elements.forEach((element) => {
    if (!element.statut) return;
    counts[element.statut] = (counts[element.statut] ?? 0) + 1;
  });
  const total = state.elements.length;
  return { counts, total };
});

watch(
  () => state.selectedModeleId,
  async (modeleId) => {
    if (modeleId) {
      await loadModele(modeleId);
    }
  }
);

onMounted(async () => {
  initializeViewer();
  
  // Charger automatiquement si des paramètres sont présents dans l'URL
  const siteIdParam = route.query.siteId as string | undefined;
  const modeleIdParam = route.query.modeleId as string | undefined;
  
  if (siteIdParam) {
    state.siteId = siteIdParam;
    await loadModeles();
    
    // Si un modeleId est spécifié, le sélectionner après le chargement
    if (modeleIdParam && state.modeles.length > 0) {
      const modeleId = parseInt(modeleIdParam, 10);
      if (!isNaN(modeleId)) {
        // Vérifier que le modèle existe dans la liste
        const modele = state.modeles.find(m => m.id === modeleId);
        if (modele) {
          state.selectedModeleId = modeleId;
        }
      }
    }
  }
});

onBeforeUnmount(() => {
  disposeViewer();
  if (currentObjectUrl) {
    URL.revokeObjectURL(currentObjectUrl);
  }
});

async function loadModeles() {
  if (!state.siteId) {
    state.error = 'Veuillez fournir un identifiant de site.';
    return;
  }
  state.error = '';
  state.loadingModeles = true;
  state.modeles = [];
  state.selectedModeleId = null;
  state.elements = [];
  try {
    const payload = await apiGet<{ items: ModeleSite[] }>(
      `/sites/${state.siteId}/modeles`
    );
    const modeles = payload.items ?? [];
    state.modeles = modeles;
    if (!modeles.length) {
      state.error = 'Aucun modèle trouvé pour ce site.';
      return;
    }
    const actuel = modeles.find((modele) => modele.estActuel) ?? modeles[0];
    state.selectedModeleId = actuel.id;
  } catch (error) {
    handleError(error);
  } finally {
    state.loadingModeles = false;
  }
}

async function loadModele(modeleId: number) {
  const modele = state.modeles.find((item) => item.id === modeleId);
  if (!modele) return;

  state.loadingViewer = true;
  state.error = '';

  try {
    resetViewer();
    const fileUrl = await downloadIfc(modele.cheminIfc);
    currentObjectUrl = fileUrl;
    await viewer?.IFC.loadIfcUrl(fileUrl, true);
    await fetchElements(modeleId);
  } catch (error) {
    handleError(error);
  } finally {
    state.loadingViewer = false;
  }
}

async function fetchElements(modeleId: number) {
  try {
    const payload = await apiGet<{ items: ElementInfo[] }>(
      `/modeles/${modeleId}/elements`
    );
    state.elements = payload.items ?? [];
  } catch (error) {
    handleError(error);
  }
}

function initializeViewer() {
  if (!viewerContainer.value) return;
  viewer = new IfcViewerAPI({
    container: viewerContainer.value,
    backgroundColor: new Color(0xf8fafc)
  });
  viewer.grid.setGrid();
  viewer.axes.setAxes();
  viewer.IFC.setWasmPath('/wasm/');
}

function resetViewer() {
  disposeViewer();
  if (viewerContainer.value) {
    viewerContainer.value.innerHTML = '';
  }
  initializeViewer();
}

function disposeViewer() {
  if (viewer) {
    viewer.dispose?.();
    viewer = null;
  }
}

async function downloadIfc(relativePath: string) {
  if (!relativePath) {
    throw new Error('Chemin IFC invalide');
  }
  // cheminIfc en base = "ifc/uuid.ifc" → URL /files/ifc/uuid.ifc (sans doubler "ifc")
  const pathSegment = relativePath.replace(/^ifc\/?/, '');
  const response = await apiFetch(
    `/files/ifc/${encodeStoragePath(pathSegment)}`,
    { 
      responseType: 'blob',
      headers: getAuthHeaders()
    }
  );
  if (!(response instanceof Blob)) {
    throw new Error('Le fichier IFC est introuvable');
  }
  if (currentObjectUrl) {
    URL.revokeObjectURL(currentObjectUrl);
  }
  return URL.createObjectURL(response);
}

async function apiGet<T>(path: string): Promise<T> {
  const response = await apiFetch(path, {
    headers: getAuthHeaders()
  });
  return response as T;
}

function encodeStoragePath(input: string) {
  return input
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
}

function handleError(error: unknown) {
  console.error(error);
  state.error =
    error instanceof Error ? error.message : 'Une erreur est survenue.';
}

function goToProjects() {
  router.push({ name: 'projets' });
}
</script>

<style scoped>
.viewer-shell {
  display: flex;
  flex-direction: column;
  height: 100vh;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
}

.app-content {
  flex: 1;
  min-height: 0;
  display: flex;
}

.viewer-panel {
  background: var(--color-bg-primary);
  border-radius: var(--radius-lg);
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.viewer-canvas {
  width: 100%;
  height: 100%;
  position: relative;
}

.viewer-panel canvas {
  width: 100% !important;
  height: 100% !important;
}

.viewer-status {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(248, 250, 252, 0.85);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  z-index: 10;
}

.bottom-panel {
  background: var(--color-bg-primary);
  padding: var(--spacing-lg);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.status-section {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-md);
  width: 100%;
}

.status-section h3 {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  flex-shrink: 0;
}

.actions {
  margin-left: auto;
  flex-shrink: 0;
}

.legend {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--spacing-md);
}

.legend li {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-sm);
  flex-shrink: 0;
}

.legend .color {
  width: 16px;
  height: 16px;
  border-radius: var(--radius-sm);
  flex-shrink: 0;
}

.legend strong {
  color: var(--color-text-primary);
}

.legend small {
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-medium);
}

.error-banner {
  position: fixed;
  bottom: var(--spacing-lg);
  left: 50%;
  transform: translateX(-50%);
  background: #fee;
  color: #c33;
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  z-index: 1000;
  max-width: 90%;
  text-align: center;
}

.empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
}
</style>

